import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { ApiConfiguration } from '@/core/api/api-configuration';
import { BaseService } from '@/core/api/base-service';
import { environment } from '@env/environment';

import { AplicarPerfilResponse, Perfil, PerfilEdicaoResponse, PerfilFromUserRequest, PerfilRequest, PerfilResumo, PerfilUsuario, SincronizacaoPreview, UsuarioPerfil } from './perfis.model';

@Injectable({ providedIn: 'root' })
export class PerfisService extends BaseService {
    readonly PATH = environment.basePath + '/sistema/perfis';

    constructor(config: ApiConfiguration, http: HttpClient) {
        super(config, http, environment.apis.apiSicom);
    }

    /**
     * Lista os perfis de acesso, com filtro opcional por status (S/N), nome e UF — `GET /sistema/perfis`.
     *
     * <p>`uf` ausente significa "todas" e só vale para o operador do CFM: ao regional o servidor impõe
     * a UF dele e descarta o parâmetro em silêncio.</p>
     */
    listar(inAtivo?: string, nome?: string, uf?: string): Observable<PerfilResumo[]> {
        let params = new HttpParams();
        if (inAtivo != null) params = params.set('inAtivo', inAtivo);
        if (nome != null) params = params.set('nome', nome);
        if (uf != null) params = params.set('uf', uf);
        return this.http.get<PerfilResumo[]>(this.PATH, { params });
    }

    /** Busca um perfil por ID — `GET /sistema/perfis/{id}`. */
    buscar(id: number): Observable<Perfil> {
        return this.http.get<Perfil>(`${this.PATH}/${id}`);
    }

    /** Cria um novo perfil — `POST /sistema/perfis`. */
    criar(req: PerfilRequest): Observable<Perfil> {
        return this.http.post<Perfil>(this.PATH, req);
    }

    /** Cria um perfil a partir das roles atuais de um usuário — `POST /sistema/perfis/de-usuario`. */
    criarDeUsuario(req: PerfilFromUserRequest): Observable<Perfil> {
        return this.http.post<Perfil>(`${this.PATH}/de-usuario`, req);
    }

    /** Prévia (read-only) do impacto de sincronizar as roles pretendidas — `POST /sistema/perfis/{id}/preview-sincronizacao`. */
    previewSincronizacao(id: number, roles: string[]): Observable<SincronizacaoPreview> {
        return this.http.post<SincronizacaoPreview>(`${this.PATH}/${id}/preview-sincronizacao`, { roles });
    }

    /** Edita um perfil — `PUT /sistema/perfis/{id}?sincronizar=true|false`. */
    editar(id: number, req: PerfilRequest, sincronizar: boolean): Observable<PerfilEdicaoResponse> {
        const params = new HttpParams().set('sincronizar', String(sincronizar));
        return this.http.put<PerfilEdicaoResponse>(`${this.PATH}/${id}`, req, { params });
    }

    /** Inativa um perfil — `PATCH /sistema/perfis/{id}/inativar`. */
    inativar(id: number): Observable<void> {
        return this.http.patch<void>(`${this.PATH}/${id}/inativar`, null);
    }

    /** Reativa um perfil — `PATCH /sistema/perfis/{id}/ativar`. */
    ativar(id: number): Observable<void> {
        return this.http.patch<void>(`${this.PATH}/${id}/ativar`, null);
    }

    /** Aplica um perfil a um usuário — `POST /sistema/perfis/{id}/aplicar`. */
    aplicar(id: number, userId: string): Observable<AplicarPerfilResponse> {
        return this.http.post<AplicarPerfilResponse>(`${this.PATH}/${id}/aplicar`, { userId });
    }

    /** Usuários vinculados a um perfil, ordenados por nome — `GET /sistema/perfis/{id}/usuarios`. */
    usuariosDoPerfil(id: number): Observable<PerfilUsuario[]> {
        return this.http.get<PerfilUsuario[]>(`${this.PATH}/${id}/usuarios`);
    }

    /** Remove um perfil aplicado a um usuário — `DELETE /sistema/perfis/usuarios/{userId}/perfis/{idPerfil}`. */
    removerDoUsuario(userId: string, idPerfil: number): Observable<void> {
        return this.http.delete<void>(`${this.PATH}/usuarios/${userId}/perfis/${idPerfil}`);
    }

    /** Exclui um perfil por lápide (roles já atribuídas são mantidas) — `DELETE /sistema/perfis/{id}`. */
    excluir(id: number): Observable<void> {
        return this.http.delete<void>(`${this.PATH}/${id}`);
    }

    /** Perfis de um usuário, com as roles de cada um — `GET /sistema/perfis/usuarios/{userId}/perfis`. */
    perfisDoUsuario(userId: string): Observable<UsuarioPerfil[]> {
        return this.http.get<UsuarioPerfil[]>(`${this.PATH}/usuarios/${userId}/perfis`);
    }
}
